import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** Réessaie le chargement sans recharger toute la page (remonte les routes pour relancer l'import lazy). */
  onRetry?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/** Détecte si l'erreur vient d'un échec de chargement de chunk ou du réseau (import dynamique, 404, etc.). */
function isChunkLoadError(error: Error | null): boolean {
  if (!error?.message) return false;
  const msg = error.message.toLowerCase();
  return (
    msg.includes('loading chunk') ||
    msg.includes('chunkloaderror') ||
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('importing a module script failed') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('dynamically imported module') ||
    msg.includes('load failed') ||
    msg.includes('failed to fetch') ||
    msg.includes('networkerror') ||
    msg.includes('error loading module') ||
    msg.includes('import script error') ||
    (msg.includes('fetch') && msg.includes('failed'))
  );
}

/**
 * Affiche un fallback au lieu d'une page blanche si un composant enfant lève une erreur.
 * Utile sur mobile (Capacitor) pour éviter l'écran noir/blanc après le splash.
 * Si onRetry est fourni, le bouton "Recharger" réessaie d'abord sans recharger la page.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary]', error, errorInfo);
  }

  handleRetry = () => {
    // Pour une erreur de chunk/réseau, le rechargement complet donne une nouvelle chance à l'import.
    if (isChunkLoadError(this.state.error)) {
      window.location.reload();
      return;
    }
    if (this.props.onRetry) {
      this.props.onRetry();
      this.setState({ hasError: false, error: null });
    } else {
      window.location.reload();
    }
  };

  handleFullReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      const isChunk = isChunkLoadError(this.state.error);
      return (
        <div className="min-h-screen w-full flex flex-col items-center justify-center p-6 bg-gray-100 dark:bg-gray-950 text-gray-900 dark:text-gray-100">
          <p className="text-lg font-semibold mb-2">Une erreur est survenue</p>
          <p className="text-sm text-gray-600 dark:text-gray-400 text-center max-w-md mb-6">
            {isChunk
              ? "Le chargement de la page a échoué (réseau ou cache). Rechargez la page (F5) pour réessayer."
              : "Une page ou un lien peut être en panne. Réessayez ou rechargez complètement la page (F5)."}
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            {!isChunk && this.props.onRetry && (
              <button
                type="button"
                onClick={this.handleRetry}
                className="px-4 py-2 rounded-lg bg-primary-500 text-white font-medium"
              >
                Réessayer
              </button>
            )}
            <button
              type="button"
              onClick={this.handleFullReload}
              className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Recharger la page (F5)
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
